import * as React from "react";
import {
  Grid,
  Typography,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import { IPrescriberDetails } from "../types/PrescriptionIngestionTypes";
import { PRESCRIBERS } from "../config/PrescriptionDefaults";

interface IProps {
  selectedPrescriber: string;
  prescriberDetails: IPrescriberDetails;
  onPrescriberChange: (selectedKey: string) => void;
  additionalPrescribers?: { [key: string]: IPrescriberDetails };
}

const PrescriberSelector: React.FC<IProps> = ({
  selectedPrescriber,
  prescriberDetails,
  onPrescriberChange,
  additionalPrescribers = {},
}) => {
  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onPrescriberChange(event.target.value as string);
  };

  // Combine default prescribers with additional ones
  const allPrescribers = { ...PRESCRIBERS, ...additionalPrescribers };

  return (
    <Grid item xs={12}>
      <Paper
        style={{ padding: 24, backgroundColor: "#f5f5f5", borderRadius: 8 }}
      >
        <Typography
          variant="h6"
          gutterBottom
          style={{
            color: "black",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <LocalHospitalIcon style={{ color: "#546e7a" }} />
          Prescriber Details
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginBottom: 16 }}
        >
          Source of Truth - Select from Dropdown
        </Typography>
        <Divider style={{ marginBottom: 20 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" size="medium">
              <InputLabel>Select Prescriber</InputLabel>
              <Select
                value={selectedPrescriber}
                onChange={handleChange}
                label="Select Prescriber"
              >
                {Object.entries(allPrescribers).map(([key, prescriber]) => (
                  <MenuItem key={key} value={key}>
                    Dr. {prescriber.firstName} {prescriber.lastName} -{" "}
                    {prescriber.clinicName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Paper
              style={{
                padding: 16,
                backgroundColor: "#ffffff",
                borderLeft: "4px solid #546e7a",
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                style={{
                  color: "black",
                  fontWeight: 600,
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <LocalHospitalIcon style={{ color: "#546e7a", fontSize: 20 }} />
                Selected Prescriber Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Name
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    Dr. {prescriberDetails.firstName}{" "}
                    {prescriberDetails.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    DEA Number
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prescriberDetails.deaNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    NPI
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prescriberDetails.npi}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    SPI
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prescriberDetails.spi}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Clinic
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prescriberDetails.clinicName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={5}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Address
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prescriberDetails.addressLine1}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    City, State
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prescriberDetails.city}, {prescriberDetails.state}{" "}
                    {prescriberDetails.zipCode}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Phone
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {prescriberDetails.phoneNumber}
                  </Typography>
                </Grid>
                {prescriberDetails.fax && prescriberDetails.fax.length > 0 && (
                  <Grid item xs={12} sm={6} md={12}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Fax
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {prescriberDetails.fax.map((f) => f.Number).join(", ")}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default PrescriberSelector;
